import * as React from 'react';
import { NumberFormatValues } from 'react-number-format';
import { extractFormCellProps, FormCell, IFormCellProps } from '../FormCell';
import { CurrencyInput, ICurrencyInputValues } from './CurrencyInput';
import { NumberInputStyled } from './NumberInput.styles';
import { FlexRow } from '../FlexRow';
import { mergeTests } from '../../test-utilities/mergeTests';
import { convertCurrencyToCents } from '../Currency';
import { convertNullToUndefined } from '../../utilities/convertNullToUndefined';
import { isNil } from 'lodash';
import { IconType } from '../Icon';
import styled from 'styled-components';
import { useEffect, useState } from 'react';
import useDebounce from '../../../utilities/hooks/useDebounce';

const StyledFlexRow = styled(FlexRow)`
  position: relative;
`;

export type TNumberInputModes = 'default' | 'currency';

const onValueChange = (props: INumberInputProps, floatValue?: number | null) => {
  if (props.onChange) {
    const error = !floatValue && props.isRequired ? `${props.label || ''} cannot be blank` : null;
    if (typeof floatValue === 'number' && !!props.valueIsCents) {
      const isNegative = floatValue < 0;
      const [integerPartString, decimalPartString] = !isNil(floatValue) ? floatValue.toString().split('.') : [];
      const integerPart = parseInt(integerPartString) * 100;
      const floatPartString =
        typeof decimalPartString === 'string' && decimalPartString.length === 1
          ? decimalPartString.padEnd(2, '0')
          : decimalPartString || '00';
      const floatPart = parseFloat(`${floatPartString.substring(0, 2)}.${floatPartString.substring(2)}`);
      props.onChange(integerPart + floatPart * (isNegative ? -1 : 1), error);
    } else {
      props.onChange(typeof floatValue === 'number' ? floatValue : null, error);
    }
  }
};

export interface INumberInputProps extends IFormCellProps {
  /** Different visual type of input. */
  mode?: TNumberInputModes;
  /** HTML 'name' attribute. */
  name?: string;
  /** Whether to allow negative numbers or not. */
  allowNegative?: boolean;
  /** The value attribute of underlying input. */
  value?: number | null;
  /** Add thousand separators on number. */
  thousandSeparator?: boolean;
  /** If defined it limits to given decimal scale. */
  decimalScale?: number;
  /** Disables the input and prevents editing the contents. */
  disabled?: boolean;
  /** If true it add 0s to match given decimalScale. */
  fixedDecimalScale?: boolean;
  /** Add a prefix before the number. */
  prefix?: string;
  /** Add a suffix after the number. */
  suffix?: string;
  /** Text that will appear in an empty input. */
  placeholder?: string;
  /** This callback exposes the input reference / DOM node to parent components. */
  getInputRef?: (elem: HTMLInputElement) => void;
  /** A callback triggered when component is focused. */
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  /** A callback triggered when focus is removed. */
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  /** A callback fired when the value prop changes. */
  onChange?: (value: number | null, error?: string | null) => void;
  /** Switches the input to read only which prevents editing the contents. */
  readOnly?: boolean;
  /** When set to true this input will gain focus whenever it's first rendered */
  autoFocus?: boolean;
  /** Set to true if the value is a cents integer that should display as currency */
  valueIsCents?: boolean;
  /** Set this to keep user from inputting a number above the max */
  max?: number;
  /** Append this icon type */
  appendIconType?: IconType;
  debounceOnValueChange?: boolean;
  format?: string | null;
}

/**
 * The HTML number input with a label, error messaging and additional text.
 */
export const NumberInput: React.FC<INumberInputProps> = (props) => {
  const { formCellProps, inputProps: extractedInputProps } = extractFormCellProps(props);
  const defaultProp: INumberInputProps = {
    allowNegative: false,
    decimalScale: 0,
    disabled: false,
    fixedDecimalScale: true,
    value: null,
    isRequired: false,
    placeholder: '',
    thousandSeparator: false,
  };
  const currencyProp: INumberInputProps = {
    ...defaultProp,
    allowNegative: true,
    decimalScale: 2,
    thousandSeparator: true,
  };
  /** FYI: Removing onChange here since we handle it outside of the control in onValueChange or onCurrencyChange
   *  Removing valueIsCents since it is not used in all child components */
  const { onChange, valueIsCents, ...inputProps } = Object.assign(
    props.mode === 'currency' ? currencyProp : defaultProp,
    extractedInputProps
  );
  const dataTestClass = inputProps['data-test-class'] || 'number-input';
  const [inputValue, setInputValue] = useState<number | undefined | null>(props.value);
  const fieldValue = props.debounceOnValueChange ? inputValue : props.value;
  const updateValue = useDebounce(inputValue, 500);

  useEffect(() => {
    if (props.debounceOnValueChange && updateValue !== props.value) {
      onValueChange(props, updateValue);
    }
    // eslint-disable-next-line
  }, [updateValue]);

  const onInputValueChange = (val: NumberFormatValues | ICurrencyInputValues) => {
    setInputValue(val.floatValue);
    if (!props.debounceOnValueChange) {
      onValueChange(props, val.floatValue);
    }
  };

  const onInputBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const { onBlur } = props;
    if (onBlur) {
      onBlur(event);
    }
  };

  const onInputFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    const { onFocus } = props;
    if (onFocus) {
      onFocus(event);
    }
  };

  useEffect(() => {
    if (props.value !== inputValue) {
      if ((props.debounceOnValueChange && props.value !== updateValue) || !props.debounceOnValueChange) {
        setInputValue(props.value);
      }
    }
    // eslint-disable-next-line
  }, [props.value]);

  return (
    <FormCell {...formCellProps} data-test-id={mergeTests(inputProps['data-test-id'])} data-test-class={dataTestClass}>
      <StyledFlexRow>
        {inputProps.prefix ? (
          <span data-test-id={mergeTests(inputProps['data-test-id'], 'prefix')}>{inputProps.prefix}</span>
        ) : null}
        {inputProps.mode === 'currency' ? (
          <CurrencyInput
            {...inputProps}
            value={fieldValue}
            onFocus={onInputFocus}
            onBlur={onInputBlur}
            hasAppendIcon={!!props.appendIconType}
            onChange={onInputValueChange}
            data-test-id={mergeTests(inputProps['data-test-id'], 'input')}
            data-test-class={dataTestClass}
            valueIsCents={valueIsCents}
            showErrorHighlight={!!formCellProps.errorText}
          />
        ) : (
          <NumberInputStyled
            {...inputProps}
            hasAppendIcon={!!inputProps.appendIconType}
            autoFocus={inputProps.autoFocus}
            onFocus={onInputFocus}
            onBlur={onInputBlur}
            data-test-id={mergeTests(inputProps['data-test-id'], 'input')}
            data-test-class={dataTestClass}
            displayType="input"
            isNumericString={
              (valueIsCents && typeof inputProps.value === 'number') || typeof inputProps.value === 'string'
            }
            onValueChange={onInputValueChange}
            showErrorHighlight={!!formCellProps.errorText}
            thousandSeparator={inputProps.thousandSeparator ? ',' : ''}
            value={
              valueIsCents && typeof fieldValue === 'number'
                ? convertCurrencyToCents(fieldValue)
                : convertNullToUndefined(fieldValue)
            }
          />
        )}
        {inputProps.suffix ? (
          <span data-test-id={mergeTests(inputProps['data-test-id'], 'suffix')}>{inputProps.suffix}</span>
        ) : null}
      </StyledFlexRow>
    </FormCell>
  );
};
